import React, { useState } from 'react';
import { TextField, Button, Typography, Container, Box, Paper, Alert, InputAdornment, IconButton, Link } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import { FirebaseError } from 'firebase/app';
import LoadingScreen from '../components/LoadingScreen.js';
import { useTranslation } from 'react-i18next';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import LoginIcon from '@mui/icons-material/Login';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [debugLog, setDebugLog] = useState<string[]>([]);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const { signIn, setUserType } = useAuth();
  const { t } = useTranslation();

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Helper function to add debug messages
  const addDebug = (message: string) => {
    console.log(message);
    setDebugLog(prev => [...prev, message]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setError('');
      setLoading(true);
      setDebugLog([]);
      
      addDebug(`Forsøger at logge ind med: ${email}`);
      
      const userCredential = await signIn(email, password);
      
      addDebug(`Login vellykket. UID: ${userCredential.user.uid}`);
      
      // Hent brugerens metadata fra users collection
      const userId = userCredential.user.uid;
      const userRef = doc(db, "users", userId);
      
      try {
        const userDoc = await getDoc(userRef);
      
        if (userDoc.exists()) {
          const userData = userDoc.data();
          addDebug(`Brugerdata hentet: ${JSON.stringify(userData)}`);
          
          // Set brugertype og bruger-id i AuthContext
          setUserType(userData.role);
          addDebug(`Brugertype sat til: ${userData.role}`);
          
          // Redirect baseret på brugerrolle
          if (userData.role === 'agent') {
            addDebug('Bruger er agent, navigerer til agent-dashboard');
            navigate('/agent-dashboard');
          } else if (userData.role === 'management') {
            addDebug('Bruger er management, navigerer til management-dashboard');
            // Ekstra sikkerhedscheck for at sikre management-bruger har nødvendige felter
            if (!userData.dataCollection) {
              addDebug('Management bruger mangler dataCollection felt, men fortsætter alligevel');
            }
            navigate('/management-dashboard');
          } else {
            // Ukendt rolle
            addDebug(`Ugyldig brugerrolle: ${userData.role || 'ingen rolle'}`);
            setError(t('login.invalidRole'));
          }
        } else {
          // Bruger findes i Authentication men ikke i users collection
          addDebug('Brugerprofil ikke fundet i Firestore collection: users');
          setError(t('login.profileNotFound'));
        }
      } catch (firestoreError) {
        console.error("Firestore fejl:", firestoreError);
        addDebug(`Firestore fejl: ${firestoreError}`);
        setError(t('login.failedToFetch'));
      }
      
    } catch (err: any) {
      console.error("Login fejl:", err);
      addDebug(`Login fejl: ${err instanceof FirebaseError ? err.code : err}`);
      
      if (err instanceof FirebaseError) {
        // Håndter Firebase Authentication fejl
        switch(err.code) {
          case 'auth/invalid-credential':
            setError(t('login.invalidCredentials'));
            break;
          case 'auth/user-not-found':
            setError(t('login.userNotFound'));
            break;
          case 'auth/wrong-password':
            setError(t('login.wrongPassword'));
            break;
          case 'auth/too-many-requests':
            setError(t('login.tooManyAttempts'));
            break;
          default:
            setError(t('errorMessages.loginFailed') + err.message);
        }
      } else {
        setError(t('errorMessages.loginFailed') + (err.message || 'Ukendt fejl'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <LoadingScreen isVisible={loading} />
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        minHeight: '100vh',
        background: 'radial-gradient(circle at center, #222851 0%, #141b2d 80%, #0c111b 100%)',
        backgroundSize: 'cover',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        padding: 3
      }}>
        <Container maxWidth="sm" sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center', 
          justifyContent: 'center',
          height: '100%'
        }}>
          <Paper elevation={6} sx={{ 
            width: '100%', 
            maxWidth: 450,
            borderRadius: 2,
            backgroundColor: '#f5f5f7',
            padding: 4,
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)'
          }}>
            <Typography 
              variant="h4" 
              component="h1" 
              align="center" 
              gutterBottom 
              sx={{ 
                fontWeight: 600,
                color: '#333',
                mb: 1
              }}
            >
              Welcome Back
            </Typography>

            <Typography 
              variant="body1" 
              align="center" 
              sx={{ 
                color: '#666',
                mb: 4
              }}
            >
              Sign in to continue to Voice Victor
            </Typography>
            
            {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 1 }}>{error}</Alert>}
            
            <Box component="form" onSubmit={handleSubmit}>
              <Typography 
                component="label" 
                htmlFor="email" 
                sx={{ 
                  fontWeight: 500, 
                  mb: 1, 
                  display: 'block',
                  color: '#444'
                }}
              >
                Email
              </Typography>
              <TextField
                required
                fullWidth
                id="email"
                name="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@example.com"
                variant="outlined"
                sx={{
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                    borderRadius: 1,
                    '& fieldset': {
                      borderColor: '#e0e0e0',
                    },
                    '&:hover fieldset': {
                      borderColor: '#bdbdbd',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#9575cd',
                    },
                  },
                  '& .MuiInputBase-input': {
                    padding: '14px 16px',
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Box component="span" sx={{ color: '#9e9e9e', fontSize: '18px' }}>
                        ✉
                      </Box>
                    </InputAdornment>
                  ),
                }}
              />

              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                mb: 1
              }}>
                <Typography 
                  component="label" 
                  htmlFor="password" 
                  sx={{ 
                    fontWeight: 500,
                    color: '#444'
                  }}
                >
                  Password
                </Typography>
                <Link 
                  href="#" 
                  underline="hover" 
                  sx={{ 
                    fontSize: '0.875rem',
                    color: '#9575cd'
                  }}
                  onClick={(e) => e.preventDefault()}
                >
                  Forgot password?
                </Link>
              </Box>

              <TextField
                required
                fullWidth
                name="password"
                type={showPassword ? "text" : "password"}
                id="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                variant="outlined"
                sx={{
                  mb: 4,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                    borderRadius: 1,
                    '& fieldset': {
                      borderColor: '#e0e0e0',
                    },
                    '&:hover fieldset': {
                      borderColor: '#bdbdbd',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#9575cd',
                    },
                  },
                  '& .MuiInputBase-input': {
                    padding: '14px 16px',
                  }
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={togglePasswordVisibility}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading}
                startIcon={<LoginIcon />}
                sx={{ 
                  py: 1.5,
                  backgroundColor: '#9575cd',
                  borderRadius: 1,
                  textTransform: 'none',
                  fontSize: '1rem',
                  fontWeight: 500,
                  boxShadow: '0 2px 10px rgba(149, 117, 205, 0.4)',
                  '&:hover': {
                    backgroundColor: '#7e57c2',
                  },
                  mb: 3
                }}
              >
                Sign In
              </Button>

              <Typography align="center" sx={{ color: '#666' }}>
                Don't have an account? <Link href="#" underline="hover" sx={{ color: '#9575cd' }}>Sign up</Link>
              </Typography>
            </Box>
            
            {/* Debug log (skjult som standard, men kan vises via en knap hvis nødvendigt) */}
            {debugLog.length > 0 && (
              <Box 
                mt={4} 
                p={2} 
                bgcolor="#f0f0f0" 
                borderRadius={1}
                border="1px solid #e0e0e0"
                sx={{ display: 'none' }} // Skjult som standard
              >
                <Typography variant="subtitle2" gutterBottom>Debug Log:</Typography>
                <Box component="pre" sx={{ 
                  fontSize: '0.75rem', 
                  maxHeight: '200px', 
                  overflow: 'auto',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all'
                }}>
                  {debugLog.map((log, i) => (
                    <div key={i}>{log}</div>
                  ))}
                </Box>
              </Box>
            )}
          </Paper>

          <Box sx={{ 
            mt: 4, 
            textAlign: 'center', 
            color: 'rgba(255,255,255,0.6)',
            fontSize: '0.875rem'
          }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              © 2025 Voice Victor. All rights reserved.
            </Typography>
            <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
              "It's not just what you say, but how you say it"
            </Typography>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default Login; 